import { inject, injectable } from 'inversify';
import type { ICasePersistent, ICaseState } from '../CasePersistance/CasePersistenceHub.ts';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm.ts';
import { TYPES } from '@/engine/ioc/types.ts';
import type { AlgorithmExtension, ValuesCollection } from '@/engine/DataFlow/DataFlow.ts';
import { action } from 'mobx';
import { DataFlowJsonIO } from '@/engine/Serialization/DataFlowJsonIO.ts';
import { DisposablesBasket } from '../../Utilities/DisposablesBasket.ts';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes.ts';
import type { ImageParams } from '../../Serialization/IImporter.ts';
import type { IImportDestination } from '../../Serialization/Importer.factory.ts';
import type { SurgeryRegistry } from '@/engine/app/registries/SurgeryRegistry.ts';
import type { IPhaseInfo, NamedParams, IActivePhase, IStateMetadata } from '@/engine/app/CasePhaseData/interfaces.ts';
import { KindInfo } from '@/engine/app/CasePhaseData/KindInfo/KindInfo.ts';

@injectable()
export class CasePhaseData implements IImportDestination, ICasePersistent {
  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _surgeryRegistry: SurgeryRegistry;

  private readonly _kindToPhases: Map<string, KindInfo> = new Map();

  private readonly _phaseToValues: Map<string, ValuesCollection> = new Map();

  private readonly _phaseToApproved: Map<string, Array<string>> = new Map();

  private readonly _phaseParamsUpdatesSubscriptions = new DisposablesBasket();

  private _kindToPhaseParams: Map<string, ImageParams> | undefined;

  public constructor(
    @inject(TYPES.PlanningAlgorithm) planningAlgorithm: PlanningAlgorithm,
    @inject(TYPES.SurgeryRegistry) surgeryRegistry: SurgeryRegistry
  ) {
    this._planningAlgorithm = planningAlgorithm;
    this._surgeryRegistry = surgeryRegistry;
  }

  private _getKindInfo(kind: string): KindInfo {
    const obj = this._kindToPhases.get(kind);
    if (!obj) {
      const newObj = new KindInfo();
      this._kindToPhases.set(kind, newObj);
      return newObj;
    } else {
      return obj;
    }
  }

  private _onKindPropUpdated(kind: string, propName: string, value: number | undefined, phaseIdx?: number): void {
    const kindInfo = this._getKindInfo(kind);
    if (!kindInfo.phaseParamsDescriptor) {
      throw new Error('Logic error');
    }
    const phaseInfo: IPhaseInfo = kindInfo.availablePhases.list[phaseIdx ?? kindInfo.activePhaseIdx];
    phaseInfo.setParam(propName, value ?? Number.NaN);
  }

  private _subscribeToPerPhaseParamsUpdate(): void {
    for (const [kind, kindInfo] of this._kindToPhases) {
      if (!kindInfo.phaseParamsDescriptor) {
        continue;
      }
      for (const paramDescriptor of kindInfo.phaseParamsDescriptor) {
        this._phaseParamsUpdatesSubscriptions.add(
          this._planningAlgorithm.addOnChangedCallbackFor<DT.NumberT>(paramDescriptor.algoName, (value) => {
            this._onKindPropUpdated(kind, paramDescriptor.paramName, value?.value);
          })
        );
      }
    }
  }

  private async _gatherPerPhaseParams(kindToPhaseParams: Map<string, ImageParams>): Promise<void> {
    for (const [kind, kindInfo] of this._kindToPhases) {
      const kindParamsInfo = kindToPhaseParams.get(kind);
      kindInfo.phaseParamsDescriptor = kindParamsInfo?.perPhaseInfo;
      if (!kindInfo.phaseParamsDescriptor) {
        continue;
      }
      const perPhaseParams = new Map<string, Array<number>>();
      for (const paramDescriptor of kindInfo.phaseParamsDescriptor) {
        const arr = new Array<number>(kindInfo.availablePhases.list.length).fill(Number.NaN);
        perPhaseParams.set(paramDescriptor.paramName, arr);
      }
      // we are determining only those inputs that are required to calculate phase params
      // Afterward, for each phase, we are setting these params and getting results - calculated phase params themselves
      const names = kindInfo.phaseParamsDescriptor.map((entry) => entry.algoName);
      const inputNames = new Set(this._planningAlgorithm.getDependentInputs(names));
      for (let i = 0, cnt = kindInfo.availablePhases.list.length; i < cnt; ++i) {
        const phaseInfo = kindInfo.availablePhases.list[i];
        const phaseValues = this._phaseToValues.get(phaseInfo.id)?.filter((value) => inputNames.has(value[0]));
        if (phaseValues) {
          await this._planningAlgorithm.setValuesAsync(phaseValues);
          for (const paramDescriptor of kindInfo.phaseParamsDescriptor) {
            const value = this._planningAlgorithm.getValue(paramDescriptor.algoName, DT.NumberT);
            this._onKindPropUpdated(kind, paramDescriptor.paramName, value?.value, i);
            const arr = perPhaseParams.get(paramDescriptor.paramName);
            if (arr && value) {
              arr[i] = value.value;
            }
          }
        }
      }
      const kindParams = kindParamsInfo?.perKindGetter(perPhaseParams);
      if (kindParams) {
        for (const [key, val] of kindParams) {
          kindInfo.setParam(key, val);
        }
        if (kindParamsInfo?.perKindInfo) {
          const values: ValuesCollection = kindParamsInfo.perKindInfo.map((info) => {
            const val = kindParams.get(info.paramName);
            return [info.algoName, val !== undefined ? new DT.NumberT(val) : undefined];
          });
          await this._planningAlgorithm.setValuesAsync(values);
        }
      }
    }
  }

  private _getInitialPhaseIdx(kind: string): number {
    const kindInfo = this._kindToPhases.get(kind);
    const initialIndexParamName = this._kindToPhaseParams?.get(kind)?.initialIndexParamName;
    if (kindInfo && initialIndexParamName) {
      const idx = kindInfo.getParam(initialIndexParamName);
      if (idx !== undefined) {
        return idx;
      }
    }
    return 0;
  }

  private _chooseActivePhases(): ValuesCollection {
    const values: ValuesCollection = [];
    for (const [kind, kindInfo] of this._kindToPhases) {
      kindInfo.activePhaseIdx = this._getInitialPhaseIdx(kind);
      const phaseInfo = kindInfo.availablePhases.list[kindInfo.activePhaseIdx];
      kindInfo.activePhase.set(phaseInfo);
      const val = this._phaseToValues.get(phaseInfo.id);
      if (!val || val.length == 0) {
        throw new Error('Phase values are expected to be valid');
      }
      values.push(...val);
      this._phaseToValues.set(phaseInfo.id, []);
    }
    return values;
  }

  private async _initParams(kindToPhaseParams?: Map<string, ImageParams>): Promise<void> {
    if (!kindToPhaseParams) {
      return;
    }
    await this._gatherPerPhaseParams(kindToPhaseParams);
    this._subscribeToPerPhaseParamsUpdate(); // after params gathering!
  }

  public registerPhase(
    kind: string,
    id: string,
    name: string,
    valuesCollection: ValuesCollection,
    weight?: number
  ): void {
    if (kind.length == 0) {
      throw new Error('Kind must be not empty');
    }
    if (id.length == 0) {
      throw new Error('Id must be not empty');
    }
    const phaseInfo = this._getKindInfo(kind).availablePhases.add(id, name);
    if (weight !== undefined) {
      phaseInfo.setParam('weight', weight);
    }
    this._phaseToValues.set(id, valuesCollection);
    this._phaseToApproved.set(id, []);
  }

  public async activateAsync(
    commonValues: ValuesCollection,
    kindToImports: Map<string, Array<string>>,
    algorithmBuilder: AlgorithmExtension,
    algorithmExtensions?: Map<string, AlgorithmExtension>,
    kindToPhaseParams?: Map<string, ImageParams>
  ): Promise<void> {
    await this._planningAlgorithm.initAlgorithmAsync(algorithmBuilder);
    if (algorithmExtensions) {
      for (const [descriptor, extension] of algorithmExtensions) {
        await this._planningAlgorithm.extendAlgorithmAsync(extension, descriptor);
      }
    }

    await this._initParams(kindToPhaseParams);
    this._setKindToPhaseParams(kindToPhaseParams);
    const values = this._chooseActivePhases();
    values.push(...commonValues);

    for (const [kind, kindInfo] of this._kindToPhases) {
      if (kindInfo.availablePhases.list.length > 1) {
        const imports = kindToImports.get(kind);
        if (!imports || imports.length == 0) {
          throw new Error('Phase imports are expected to be valid');
        }
        kindInfo.phaseImports = imports;
        const defaultInputs = this._planningAlgorithm.getDependentInputValues(kindInfo.phaseImports, true);
        for (let i = 0, cnt = kindInfo.availablePhases.list.length; i < cnt; ++i) {
          if (i == kindInfo.activePhaseIdx) {
            continue;
          }
          const phaseInfo = kindInfo.availablePhases.list[i];
          const phaseValues = this._phaseToValues.get(phaseInfo.id);
          if (!phaseValues || phaseValues.length == 0) {
            throw new Error('Phase values are expected to be valid');
          }
          phaseValues.unshift(...defaultInputs); //[chebotarev] unshift as we need defaults to appear first in the list, so that afterward they are potentially overwritten with imported values
        }
      }
    }

    await this._planningAlgorithm.setValuesAsync(values);
  }

  public async clearPlanningAlgorithmSnapshots(): Promise<void> {
    return this._planningAlgorithm.clearSnapshots();
  }

  public async switchAsync(kind: string, index?: number): Promise<string> {
    const info = this._kindToPhases.get(kind);
    if (!info) {
      throw new Error('No phases to switch');
    }

    const idFrom = info.availablePhases.list[info.activePhaseIdx].id;

    let idxTo = info.activePhaseIdx;
    if (index !== undefined) {
      // use provided phase index
      if (index >= info.availablePhases.list.length) {
        throw new Error('Non-existent phase to switch');
      }
      idxTo = index;
    } else {
      // cycle through indices
      idxTo = info.activePhaseIdx + 1;
      if (idxTo >= info.availablePhases.list.length) {
        idxTo = 0;
      }
    }

    if (idxTo == info.activePhaseIdx) {
      return idFrom;
    }

    info.activePhaseIdx = idxTo;

    const phaseInfo = info.availablePhases.list[info.activePhaseIdx];
    const idTo = phaseInfo.id;

    const values = this._phaseToValues.get(idTo);
    const approvedNames = this._phaseToApproved.get(idTo);
    if (!values || values.length == 0 || !approvedNames) {
      throw new Error('Phase values are expected to be valid');
    }

    try {
      info.setIsPhaseSwitching(true);

      this._phaseToValues.set(idFrom, this._planningAlgorithm.getDependentInputValues(info.phaseImports));
      this._phaseToApproved.set(idFrom, this._planningAlgorithm.getApprovedNames());

      await this._planningAlgorithm.switchAsync(idFrom, idTo, values, approvedNames, true, index === undefined);
    } finally {
      this._phaseToValues.set(idTo, []);
      this._phaseToApproved.set(idTo, []);

      info.activePhase.set(phaseInfo);

      info.setIsPhaseSwitching(false);
    }

    return idTo;
  }

  public async approveTuneValuesDefaults(): Promise<void> {
    await this._planningAlgorithm.approveTuneValuesDefaults();
    for (const key of Array.from(this._phaseToValues.keys())) {
      const valuesMap = new Map(this._phaseToValues.get(key));
      const approvedNames = new Array<string>();
      for (const name of Array.from(valuesMap.keys()).filter((name) => name.endsWith('.flag'))) {
        const flag = valuesMap.get(name);
        if (flag && (flag as unknown as DT.TuneFlag).value == DT.ETuneFlag.USER) {
          const tuneName = name.substring(0, name.length - 5);
          const apprName = tuneName + '.appr';
          const userName = tuneName + '.user';
          const userValue = valuesMap.get(userName);
          if (!userValue) {
            throw new Error('User value expected to be valid');
          }
          valuesMap.set(apprName, userValue);
          valuesMap.set(name, new DT.TuneFlag(DT.ETuneFlag.APPROVED));
          approvedNames.push(tuneName);
        }
      }
      this._phaseToValues.set(key, Array.from(valuesMap));
      this._phaseToApproved.set(key, approvedNames);
    }
  }

  public registerInputs(kind: string, values: ValuesCollection): void {
    const info = this._kindToPhases.get(kind);
    if (!info) {
      throw new Error('Unknown kind');
    }
    for (const namedValue of values) {
      info.phaseImports.push(namedValue[0]);
      for (const { id } of info.availablePhases.list) {
        const phaseValues = this._phaseToValues.get(id);
        if (!phaseValues) {
          throw new Error('Logic Error');
        }
        phaseValues.push(namedValue);
      }
    }
  }

  public unregisterInputs(kind: string, names: Array<string>): void {
    const info = this._kindToPhases.get(kind);
    if (!info) {
      throw new Error('Unknown kind');
    }
    const namesSet = new Set(this._planningAlgorithm.getDependentInputValues(names).map((namedValue) => namedValue[0]));
    info.phaseImports = info.phaseImports.filter((name) => !namesSet.has(name));
    for (const { id } of info.availablePhases.list) {
      const phaseValues = this._phaseToValues.get(id);
      const approvedNames = this._phaseToApproved.get(id);
      if (!phaseValues || !approvedNames) {
        throw new Error('Logic Error');
      }
      this._phaseToValues.set(
        id,
        phaseValues.filter((namedValue) => !namesSet.has(namedValue[0]))
      );
      this._phaseToApproved.set(
        id,
        approvedNames.filter((name) => !namesSet.has(name))
      );
    }
  }

  public getRegisteredKinds(): Array<string> {
    return Array.from(this._kindToPhases.keys());
  }

  public getAvailablePhases(kind: string): Array<string> {
    const info = this._kindToPhases.get(kind);
    if (!info) {
      throw new Error('Invalid Image Phase kind');
    }
    return info.availablePhases.list.map((entry) => entry.id);
  }

  public getAvailablePhasesInfos(kind: string): Array<IPhaseInfo> {
    const info = this._kindToPhases.get(kind);
    if (!info) {
      throw new Error('Invalid Image Phase kind');
    }
    return info.availablePhases.list;
  }

  public getIsActivePhaseSwitching(kind: string): boolean {
    const info = this._kindToPhases.get(kind);
    if (!info) {
      throw new Error('Invalid Image Phase kind');
    }
    return info.getIsPhaseSwitching();
  }

  public getAvailableKinds(): Array<string> {
    return Array.from(this._kindToPhases.keys());
  }

  public getInitialPhaseIdx(kind: string): number {
    return this._getInitialPhaseIdx(kind);
  }

  public getActivePhaseIdx(kind: string): number {
    const info = this._kindToPhases.get(kind);
    if (!info) {
      throw new Error('Invalid Image Phase kind');
    }
    return info.activePhaseIdx;
  }

  public getActivePhaseId(kind: string): string {
    const info = this._kindToPhases.get(kind);
    if (!info) {
      throw new Error('Invalid Image Phase kind');
    }
    return info.activePhase.id;
  }

  public getActivePhase(kind: string): IActivePhase {
    const info = this._kindToPhases.get(kind);
    if (!info) {
      throw new Error('Invalid Image Phase kind');
    }
    return info.activePhase;
  }

  public getParam(kind: string, paramName: string): number | undefined {
    const info = this._kindToPhases.get(kind);
    if (!info) {
      throw new Error('Invalid Image Phase kind');
    }
    return info.getParam(paramName);
  }

  @action
  public setParam(kind: string, paramName: string, paramValue: number): void {
    const info = this._kindToPhases.get(kind);
    if (!info) {
      throw new Error('Invalid Image Phase kind');
    }
    info.setParam(paramName, paramValue);
  }

  public saveState(): Promise<ICaseState> {
    const io = new DataFlowJsonIO();
    const state: ICaseState<IStateMetadata> = {
      version: '1.3',
      metadata: {
        phaseKinds: Array.from(this._kindToPhases.entries()).map((val) => {
          const [kind, kindInfo] = val;

          return {
            kind,
            kindInfo: {
              phaseImports: kindInfo.phaseImports,
              activePhaseId: kindInfo.activePhase.id,
              availablePhases: kindInfo.availablePhases.list.map((v) => {
                return {
                  id: v.id,
                  name: v.name,
                  params: Array.from(v.getParamsIterator()).map((param) => {
                    return {
                      name: param[0],
                      value: param[1]
                    };
                  })
                };
              }),
              phaseParamsDescriptor: kindInfo.phaseParamsDescriptor,
              params: Array.from(kindInfo.getParamsIterator()).map((param) => {
                return {
                  name: param[0],
                  value: param[1]
                };
              })
            }
          };
        }),
        phaseStates: Array.from(this._phaseToValues.entries()).map((val) => {
          const [phase, valuesCollection] = val;
          return {
            id: phase,
            values: valuesCollection.map((v) => {
              const type = this._planningAlgorithm.getType(v[0]);
              return {
                name: v[0],
                type: type.name,
                value: io.export(type, v[1])
              };
            }),
            approvedNames: this._phaseToApproved.get(phase) || []
          };
        })
      }
    };
    return Promise.resolve(state);
  }

  @action
  public loadState(metadata: unknown): Promise<void> {
    const io = new DataFlowJsonIO();
    const meta = metadata as IStateMetadata;
    this._setKindToPhaseParams(this._surgeryRegistry.getCurrentSurgeryMCSDImporterConfig().imageParams);
    for (const entry of meta.phaseKinds) {
      const info = this._getKindInfo(entry.kind);
      info.phaseImports = entry.kindInfo.phaseImports;
      const activePhaseId = entry.kindInfo.activePhaseId;
      for (const phase of entry.kindInfo.availablePhases) {
        const phaseParams: NamedParams = new Map();
        for (const param of phase.params) {
          phaseParams.set(param.name, param.value);
        }
        const i = info.availablePhases.add(phase.id, phase.name, phaseParams);
        if (i.id == activePhaseId) {
          info.activePhase.set(i);
          info.activePhaseIdx = info.availablePhases.list.length - 1;
        }
      }
      info.phaseParamsDescriptor = entry.kindInfo.phaseParamsDescriptor;
      for (const param of entry.kindInfo.params) {
        info.setParam(param.name, param.value);
      }
    }
    this._subscribeToPerPhaseParamsUpdate();
    for (const entry of meta.phaseStates) {
      const values: ValuesCollection = [];
      for (const val of entry.values) {
        const value = io.import(val.value, val.type, val.name);
        if (!value) {
          throw new Error('Values are expected to be valid');
        }
        values.push([val.name, value]);
      }
      this._phaseToValues.set(entry.id, values);
      this._phaseToApproved.set(entry.id, entry.approvedNames);
    }
    return Promise.resolve();
  }

  private _setKindToPhaseParams(params: Map<string, ImageParams> | undefined): void {
    this._kindToPhaseParams = params;
  }
}
